import type { ContactPoint, Location, HealthcareService } from "fhir/r3"
import { useMemo } from "react"

interface Props {
  healthcareService: HealthcareService
  header: string
  location?: Location
}

const ContactLink = ({ contactPoint }: { contactPoint: ContactPoint }) => {
  const linkProps = useMemo(() => {
    switch (contactPoint.system) {
      case "phone":
      case "fax":
      case "pager":
        return {
          href: `tel:+44${contactPoint?.value?.replaceAll(" ", "")}`,
          action:
            contactPoint.system === "phone"
              ? "Contact the clinic on"
              : contactPoint.system === "fax"
                ? "Fax the clinic on"
                : "Page the clinic on",
        }
      case "email":
        return {
          href: `mailto:${contactPoint.value}`,
          action: "Email",
        }
      case "sms":
        return {
          href: `sms:+44${contactPoint.value?.replaceAll(" ", "")}`,
          action: "Text the clinic on",
        }
      case "url":
        return {
          href: contactPoint.value,
          action: "Visit",
        }
      default:
        return {
          href: undefined,
          action: "Contact the clinic via",
        }
    }
  }, [contactPoint.system, contactPoint.value])

  const { href, action } = linkProps

  return href ? (
    <a href={href}>
      {action} {contactPoint.value}
    </a>
  ) : (
    <p>
      {action} {contactPoint.value}
    </p>
  )
}

/**
 * This component is similar to ContactDetails, but purposely renders a SINGLE point of contact link that is clickable.
 */
export const OtherWaysTo = ({ header, healthcareService, location }: Props) => {
  const getContactPoint = (): ContactPoint | undefined => {
    // If a location resource exists, it takes priority over HealthCareService
    if (location?.telecom?.length && location?.telecom?.length > 0) {
      const firstRankContact = location.telecom?.find((it) => it?.rank === 1)
      if (firstRankContact) {
        return firstRankContact
      }

      const phone = location.telecom?.find((it) => it.system === "phone")
      if (phone) {
        return phone
      }
    }

    const firstRankContact = healthcareService.telecom?.find((it) => it?.rank === 1)
    if (firstRankContact) {
      return firstRankContact
    }

    const phone = healthcareService.telecom?.find((it) => it.system === "phone")
    if (phone) {
      return phone
    }

    return healthcareService.telecom?.find((it) => it.system === "email")
  }

  const contactPoint = getContactPoint()

  if (!contactPoint) return null

  return (
    <>
      <h2 className="nhsuk-heading-l">{header}</h2>
      <ContactLink contactPoint={contactPoint} />
    </>
  )
}
